import { IconCircleCheckFilled } from '@tabler/icons-react'
import {
  App,
  Button,
  Form,
  Input,
  Space,
} from 'antd'
import Password from 'antd/es/input/Password'
import Title from 'antd/es/typography/Title'
import {
  confirmResetPassword,
  resetPassword,
  ResetPasswordOutput,
} from 'aws-amplify/auth'
import classNames from 'classnames'
import React, { useState } from 'react'

import {
  email,
  required,
} from '@/utils/rules'

import styles from './forget-password.module.css'


export interface ForgetPasswordBlockProps {
  onClose?: () => void;
}

export default function ForgetPasswordBlock({ onClose }: ForgetPasswordBlockProps) {
  const [resetSuccess, setResetSuccess] = useState(false)

  function handlePasswordReset() {
    setResetSuccess(true)
  }

  function renderSignInInfo() {
    return (
      <Space direction="vertical" align="center" className={styles.signInInfo}>
        <IconCircleCheckFilled size={80} style={{ color: '#06ce00' }} />
        <Title level={2}>Password Reset</Title>
        <p>
          Your password has been reset successfully. <br />
          Please sign in with your new password.
        </p>
        <p>
          <Button
            type="primary"
            onClick={() => {
              onClose?.()
              setResetSuccess(false)
            }}
            size="large"
          >
            Sign In
          </Button>
        </p>
      </Space>
    )
  }

  function renderForm() {
    return (
      <div>
        <ForgetPasswordEdit onPasswordReset={handlePasswordReset} />
        <a
          type="link"
          className={classNames(styles.cancelBtn, 'gray-link')}
          onClick={onClose}
        >
          Cancel
        </a>
      </div>
    )
  }

  return (
    <div className={styles.forgetPassworldOverylay}>
      {resetSuccess ? renderSignInInfo() : renderForm()}
    </div>
  )
}

interface FormValues {
  username: string;
  confirmationCode: string;
  newPassword: string;
}

export interface ForgetPasswordEditProps {
  onPasswordReset?: () => void;
}

export function ForgetPasswordEdit({ onPasswordReset }: ForgetPasswordEditProps) {
  const [form] = Form.useForm<FormValues>()
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  function handleResetPasswordNextSteps(output: ResetPasswordOutput) {
    const { nextStep } = output
    if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
      const codeDeliveryDetails = nextStep.codeDeliveryDetails
      void message.info(
        `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`,
      )
    }
  }

  async function onFinish(values: FormValues) {
    setLoading(true)
    try {
      await confirmResetPassword(values)
      void message.success('Password reset successfully')
      onPasswordReset?.()
    } catch (ex) {
      if (ex instanceof Error) {
        void message.error(ex.message)
      }
    }
    setLoading(false)
  }

  async function handleSendConfirmationCode() {
    setSending(true)
    try {
      const { username } = await form.validateFields(['username'])
      try {
        const output = await resetPassword({ username })
        handleResetPasswordNextSteps(output)
      } catch (ex) {
        if (ex instanceof Error) {
          void message.error(
            ex.message.toLowerCase().includes('user password cannot be reset in the current state')
              ? 'User password cannot be reset if never logged in.'
              : ex.message,
          )
        }
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={styles.forgetPasswordContainer}>
      <Title level={2} className={styles.forgotPasswordTitle}>
        Forgot your password?
      </Title>
      <p>
        We get it. Things happen. Please enter the email address associated with
        your account.
      </p>
      <Form<FormValues>
        form={form}
        onFinish={onFinish}
        layout="vertical"
      >
        <Form.Item label="Email" required>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item noStyle name="username" rules={[required(), email()]}>
              <Input placeholder="Email" size="large" autoFocus />
            </Form.Item>
            <Form.Item noStyle>
              <Button
                type="primary"
                className={styles.sendCodeBtn}
                loading={sending}
                size="large"
                onClick={handleSendConfirmationCode}
              >
                {sending ? 'Sending...' : 'Send Code'}
              </Button>
            </Form.Item>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label="Confirmation Code"
          name="confirmationCode"
          rules={[required()]}
        >
          <Input placeholder="Confirmation Code" size="large" />
        </Form.Item>
        <Form.Item label="New Password" name="newPassword" rules={[required()]}>
          <Password placeholder="New Password" size="large" />
        </Form.Item>
        <Form.Item>
          <Button
            loading={loading}
            htmlType="submit"
            type="primary"
            size="large"
          >
            Reset Password
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
